// Fiche d'un agent (M11), réservée aux RH habilités : identité administrative sans paie, historique des
// affectations (une affectation ouverte est close explicitement avant la suivante), contrats, solde de
// congés par type et année avec ses mouvements, ajustement motivé.
import { LIBELLES_STATUT_AGENT, LIBELLES_TYPE_CONGE, TYPES_CONGE } from '@cc/contrats';
import { Bandeau, Bouton, Champ, Dialogue, LienBouton, Panneau, Selection, Tableau, type ColonneTableau } from '@cc/ui';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date, dateHeure } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { useServices } from '../budget/api';
import { affecterAgent, ajouterContrat, ajusterSolde, cloreAffectation, INVALIDER_PERSONNEL, useAgent, useSolde, type AffectationAgent, type ContratAgent, type MouvementSolde } from './api';
import { aujourdhuiIso, jours } from './logique';
import commun from '../../commun/commun.module.css';
import styles from '../proprete/proprete.module.css';

type Formulaire = 'affectation' | 'contrat' | null;

function DialogueAffectation({ agent, onFermer }: { agent: string; onFermer: () => void }) {
  const services = useServices();
  const [v, setV] = useState({ service_id: '', poste: '', debut: aujourdhuiIso(), fin: '' });
  const action = useAction({ executer: () => affecterAgent(agent, { ...v, fin: v.fin || null }), succes: 'Affectation enregistrée.', invalider: INVALIDER_PERSONNEL, apres: onFermer });
  return (
    <Dialogue ouvert titre="Nouvelle affectation" description="Une affectation ne chevauche aucune autre : clore d'abord l'affectation ouverte." onFermer={onFermer}
      actions={<><Bouton variante="secondaire" onClick={onFermer}>Annuler</Bouton><Bouton variante="principal" chargement={action.enCours} disabled={!v.service_id || !v.poste.trim()} onClick={() => action.lancer(undefined)}>Affecter</Bouton></>}>
      <div className={styles.grilleFormulaire}>
        <Selection libelle="Service" options={(services.data?.elements ?? []).map((s) => ({ valeur: s.id, libelle: s.nom }))} optionVide="Choisir un service" value={v.service_id} onChange={(e) => setV((x) => ({ ...x, service_id: e.target.value }))} />
        <Champ libelle="Poste" value={v.poste} onChange={(e) => setV((x) => ({ ...x, poste: e.target.value }))} required />
        <Champ libelle="Début" type="date" value={v.debut} onChange={(e) => setV((x) => ({ ...x, debut: e.target.value }))} required />
        <Champ libelle="Fin" type="date" value={v.fin} onChange={(e) => setV((x) => ({ ...x, fin: e.target.value }))} facultatif />
      </div>
      {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </Dialogue>
  );
}

function DialogueContrat({ agent, onFermer }: { agent: string; onFermer: () => void }) {
  const [v, setV] = useState({ reference: '', nature: '', debut: aujourdhuiIso(), fin: '' });
  const action = useAction({ executer: () => ajouterContrat(agent, { ...v, fin: v.fin || null }), succes: 'Contrat enregistré.', invalider: INVALIDER_PERSONNEL, apres: onFermer });
  return (
    <Dialogue ouvert titre="Nouveau contrat" description="Aucun élément de rémunération : référence, nature et période seulement. Une alerte est adressée aux RH 60 jours avant la fin." onFermer={onFermer}
      actions={<><Bouton variante="secondaire" onClick={onFermer}>Annuler</Bouton><Bouton variante="principal" chargement={action.enCours} disabled={!v.reference.trim() || !v.nature.trim()} onClick={() => action.lancer(undefined)}>Enregistrer</Bouton></>}>
      <div className={styles.grilleFormulaire}>
        <Champ libelle="Référence" value={v.reference} onChange={(e) => setV((x) => ({ ...x, reference: e.target.value }))} required />
        <Champ libelle="Nature" value={v.nature} onChange={(e) => setV((x) => ({ ...x, nature: e.target.value }))} required />
        <Champ libelle="Début" type="date" value={v.debut} onChange={(e) => setV((x) => ({ ...x, debut: e.target.value }))} required />
        <Champ libelle="Fin" type="date" value={v.fin} onChange={(e) => setV((x) => ({ ...x, fin: e.target.value }))} facultatif />
      </div>
      {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </Dialogue>
  );
}

function DialogueAjustement({ agent, type, annee, onFermer }: { agent: string; type: string; annee: number; onFermer: () => void }) {
  const [v, setV] = useState({ jours: '', motif: '' });
  const demi = Math.round(Number(v.jours.replace(',', '.')) * 2);
  const action = useAction({
    executer: () => ajusterSolde({ agent_id: agent, type, annee, demi_journees: demi, motif: v.motif, cle_idempotence: crypto.randomUUID() }),
    succes: 'Solde ajusté.', invalider: INVALIDER_PERSONNEL, apres: onFermer,
  });
  return (
    <Dialogue ouvert titre="Ajuster le solde" description="Crédit ou correction motivé, en jours (par demi-journée) ; aucun solde ne devient négatif. Les règles d'acquisition restent à confirmer par l'administration." onFermer={onFermer}
      actions={<><Bouton variante="secondaire" onClick={onFermer}>Annuler</Bouton><Bouton variante="principal" chargement={action.enCours} disabled={!Number.isFinite(demi) || demi === 0 || v.motif.trim().length < 3} onClick={() => action.lancer(undefined)}>Enregistrer</Bouton></>}>
      <Champ libelle="Jours (négatif pour une correction, par pas de 0,5)" inputMode="decimal" value={v.jours} onChange={(e) => setV((x) => ({ ...x, jours: e.target.value }))} required />
      <Champ libelle="Motif" value={v.motif} onChange={(e) => setV((x) => ({ ...x, motif: e.target.value }))} required />
      {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </Dialogue>
  );
}

function SoldeAgent({ agent, modifiable }: { agent: string; modifiable: boolean }) {
  const [type, setType] = useState<string>('annuel');
  const [annee, setAnnee] = useState(Number(aujourdhuiIso().slice(0, 4)));
  const [ajustement, setAjustement] = useState(false);
  const solde = useSolde(agent, type, annee);
  const colonnes: ColonneTableau<MouvementSolde>[] = [
    { id: 'date', entete: 'Date', enTeteLigne: true, cellule: (m) => dateHeure(m.cree_le) },
    { id: 'nature', entete: 'Nature', cellule: (m) => m.nature },
    { id: 'jours', entete: 'Jours', numerique: true, cellule: (m) => jours(m.demi_journees) },
    { id: 'motif', entete: 'Motif', cellule: (m) => m.motif },
  ];
  return (
    <Panneau titre="Solde de congés" niveau={2} actions={modifiable ? <Bouton variante="secondaire" onClick={() => setAjustement(true)}>Ajuster le solde</Bouton> : null}>
      <div className={styles.grilleFormulaire}>
        <Selection libelle="Type" options={TYPES_CONGE.map((t) => ({ valeur: t, libelle: LIBELLES_TYPE_CONGE[t] }))} value={type} onChange={(e) => setType(e.target.value)} />
        <Champ libelle="Année" type="number" min={2000} max={2200} value={annee} onChange={(e) => setAnnee(Number(e.target.value))} />
      </div>
      <EtatRequete chargement={solde.isPending} erreur={solde.error} onReessayer={() => void solde.refetch()} lot="L5-A" fonction="Le solde de congés">
        {solde.data ? (
          <>
            <p>Solde disponible : <strong>{jours(solde.data.demi_journees)}</strong></p>
            {solde.data.mouvements.length ? <Tableau legende="Mouvements du solde" colonnes={colonnes} lignes={solde.data.mouvements} cleLigne={(m) => m.id} /> : <p>Aucun mouvement pour ce type et cette année.</p>}
          </>
        ) : null}
      </EtatRequete>
      {ajustement ? <DialogueAjustement agent={agent} type={type} annee={annee} onFermer={() => setAjustement(false)} /> : null}
    </Panneau>
  );
}

export function PageAgent() {
  const { id = '' } = useParams();
  const { contexte } = useSession();
  const fiche = useAgent(id);
  const a = fiche.data;
  const [formulaire, setFormulaire] = useState<Formulaire>(null);
  const modifiable = peut(contexte, 'personnel_sensible', 'modifier');
  const clore = useAction({ executer: (af: AffectationAgent) => cloreAffectation(id, af.id, aujourdhuiIso()), succes: 'Affectation close à la date du jour.', invalider: INVALIDER_PERSONNEL });
  const colonnesAff: ColonneTableau<AffectationAgent>[] = [
    { id: 'service', entete: 'Service', enTeteLigne: true, cellule: (af) => af.service_nom },
    { id: 'poste', entete: 'Poste', cellule: (af) => af.poste },
    { id: 'periode', entete: 'Période', cellule: (af) => `${date(af.debut)} au ${af.fin ? date(af.fin) : 'en cours'}` },
    { id: 'action', entete: 'Action', cellule: (af) => (modifiable && !af.fin ? <Bouton variante="secondaire" chargement={clore.enCours} onClick={() => clore.lancer(af)}>Clore ce jour</Bouton> : null) },
  ];
  const colonnesContrats: ColonneTableau<ContratAgent>[] = [
    { id: 'reference', entete: 'Référence', enTeteLigne: true, cellule: (c) => c.reference },
    { id: 'nature', entete: 'Nature', cellule: (c) => c.nature },
    { id: 'periode', entete: 'Période', cellule: (c) => `${date(c.debut)} au ${c.fin ? date(c.fin) : 'durée indéterminée'}` },
  ];
  return (
    <>
      <EnTetePage etiquette="Fiche d'agent" titre={a ? `${a.nom} ${a.prenoms}` : 'Agent'} description={a ? `Matricule ${a.matricule}` : null}
        actions={<LienBouton href="/personnel" icone={ArrowLeft} variante="secondaire">Personnel</LienBouton>} />
      <EtatRequete chargement={fiche.isPending} erreur={fiche.error} onReessayer={() => void fiche.refetch()} lot="L5-A" fonction="La fiche de l'agent">
        {a ? (
          <div className={commun.pile}>
            <Panneau titre="Situation administrative" niveau={2}>
              <dl className={commun.definitions}>
                <dt>Statut</dt><dd>{LIBELLES_STATUT_AGENT[a.statut]}</dd>
                <dt>Grade</dt><dd>{a.grade ?? 'non renseigné'}</dd>
                <dt>Échelon</dt><dd>{a.echelon ?? 'non renseigné'}</dd>
                <dt>Entrée</dt><dd>{date(a.date_entree)}</dd>
                <dt>Compte de connexion</dt><dd>{a.appartenance_id ? 'lié' : 'aucun'}</dd>
              </dl>
            </Panneau>
            <Panneau titre="Affectations" niveau={2} actions={modifiable ? <Bouton variante="secondaire" onClick={() => setFormulaire('affectation')}>Nouvelle affectation</Bouton> : null}>
              {clore.messageErreur ? <Bandeau type="erreur">{clore.messageErreur}</Bandeau> : null}
              {a.affectations.length ? <Tableau legende="Affectations" colonnes={colonnesAff} lignes={a.affectations} cleLigne={(af) => af.id} /> : <p>Aucune affectation enregistrée.</p>}
            </Panneau>
            <Panneau titre="Contrats" niveau={2} actions={modifiable ? <Bouton variante="secondaire" onClick={() => setFormulaire('contrat')}>Nouveau contrat</Bouton> : null}>
              {a.contrats.length ? <Tableau legende="Contrats" colonnes={colonnesContrats} lignes={a.contrats} cleLigne={(c) => c.id} /> : <p>Aucun contrat enregistré.</p>}
            </Panneau>
            <SoldeAgent agent={id} modifiable={modifiable} />
          </div>
        ) : null}
      </EtatRequete>
      {formulaire === 'affectation' ? <DialogueAffectation agent={id} onFermer={() => setFormulaire(null)} /> : null}
      {formulaire === 'contrat' ? <DialogueContrat agent={id} onFermer={() => setFormulaire(null)} /> : null}
    </>
  );
}
